/**
 * Truncates a URL while preserving its structure and replacing the middle of long IDs with ellipsis
 *
 * @param {string} url - The complete URL to truncate
 * @param {Object} options - Configuration options
 * @param {number} options.maxIdLength - Maximum length for ID before truncation (default: 8)
 * @param {number} options.startChars - Number of characters to keep at the beginning of the ID (default: 4)
 * @param {number} options.endChars - Number of characters to keep at the end of the ID (default: 4)
 * @returns {string} The truncated URL
 */
export const truncateUrl = (url: string, options: { [_: string]: number } = {}) => {
    // Default options
    const {
        maxIdLength = 8,
        startChars = 4,
        endChars = 4
    } = options;

    // Check if URL is a string and not empty
    if (!url || typeof url !== 'string') {
        return url;
    }

    try {
        // Parse the URL
        const urlObj = new URL(url);

        // Split the path into segments
        const pathSegments = urlObj.pathname.split('/').filter(segment => segment);

        // Identify if a segment looks like a UUID or a long ID
        for (let i = 0; i < pathSegments.length; i++) {
            const segment = pathSegments[i];

            // If the segment is longer than the maximum length and contains typical ID characters
            if (segment.length > maxIdLength && /[-a-f0-9]/.test(segment)) {
                // Truncate the segment
                if (segment.length > startChars + endChars) {
                    pathSegments[i] = segment.substring(0, startChars) +
                        '...' +
                        segment.substring(segment.length - endChars);
                }
            }
        }

        // Rebuild the path
        urlObj.pathname = '/' + pathSegments.join('/');

        // Return the truncated URL
        return urlObj.toString();
    } catch (error) {
        // In case of error (invalid URL), try a simpler approach
        console.warn('Error while parsing URL:', error);

        // Look for a pattern resembling a UUID in the URL
        const uuidPattern = /([a-f0-9]{8}(-[a-f0-9]{4}){3}-[a-f0-9]{12})/i;
        const match = url.match(uuidPattern);

        if (match && match[1]) {
            const uuid = match[1];
            const truncatedUuid = uuid.substring(0, startChars) + '...' + uuid.substring(uuid.length - endChars);
            return url.replace(uuid, truncatedUuid);
        }

        return url;
    }
}
